// DRPX Courier — Formatierungs-Utilities (Preise, Datum, Dateigröße)
import { MWST_SATZ } from '../config/constants.js';
export { MWST_SATZ };

/** Millisekunden pro Tag (24 × 60 × 60 × 1000). */
export const MS_PER_DAY = 86400000;

// ════════════════════════════════════════════════════════════════════
// Preisfunktionen — Preise sind netto in Euro gespeichert (numeric)
// ════════════════════════════════════════════════════════════════════
//
// Float-Drift-Problem (Audit 16.05.2026):
// JavaScript-Zahlen sind IEEE-754-Floats. Multiplikationen wie
// `350 * 0.19` liefern nicht exakt `66.5`, sondern z.B.
// `66.49999999999999`. Aggregiert über mehrere Positionen entsteht
// daraus z.B. `416.48810000000003` statt `416.50`. Das ist nicht nur
// kosmetisch — der Plausibilitäts-CHECK aus Migration 075 verlangt
// `ABS((netto + mwst) - brutto) < 0.05`, und mit reinen Floats kann
// auch das in Edge-Fällen kippen.
//
// Lösung: ALLE Berechnungen intern in CENTS (ganzzahligen Integern)
// durchführen, erst beim Speichern/Anzeigen zurück zu Euro mit
// `centsToEuro`. Die Datenbank-Spalten bleiben `numeric` in Euro —
// das DB-Schema ändert sich nicht.

/**
 * Konvertiert einen Euro-Wert in Cents (gerundet auf ganze Cents).
 *
 * Beispiele:
 *   euroToCents(350)        → 35000
 *   euroToCents(350.00)     → 35000
 *   euroToCents(350.005)    → 35001 (bankers' rounding via Math.round)
 *   euroToCents("350,50")   → 0 (NICHT für deutsche Komma-Strings!
 *                                 Eingaben vorher mit parseFloat aufbereiten)
 *   euroToCents(null)       → 0
 *   euroToCents(undefined)  → 0
 *   euroToCents(NaN)        → 0
 */
export const euroToCents = (eur: number | null | undefined): number => {
  const n = Number(eur);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};

/**
 * Konvertiert Cents zurück in einen Euro-Wert (max. 2 Nachkommastellen).
 *
 * Beispiele:
 *   centsToEuro(35000)  → 350
 *   centsToEuro(35050)  → 350.5
 *   centsToEuro(0)      → 0
 */
export const centsToEuro = (cents: number | null | undefined): number => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return 0;
  return n / 100;
};

/**
 * Berechnet den MwSt-Anteil eines Netto-Cents-Betrags in Cents (ganzzahlig).
 * MWST_SATZ ist 0.19, wir nutzen Integer-Math (× 19, ÷ 100) um Float-
 * Drift zu vermeiden.
 *
 * Beispiele (mit MWST_SATZ = 0.19):
 *   mwstCents(35000)  → 6650 (= 66,50 €)
 *   mwstCents(100)    → 19 (= 0,19 €)
 *   mwstCents(101)    → 19 (= 0,19 €, gerundet von 19,19)
 */
export const mwstCents = (nettoCents: number): number => {
  const pct = Math.round(MWST_SATZ * 100); // 19
  return Math.round((nettoCents * pct) / 100);
};

/** Gibt den Netto-Wert als Zahl zurück (0 bei null/undefined/NaN). */
export const nettoVal = (v: number | null | undefined): number => {
  return Number(v || 0);
};

/**
 * Berechnet den Brutto-Wert (Netto × (1 + MwSt)).
 * Intern via Cents-Pfad, damit keine Float-Drift entsteht.
 *
 * Beispiele:
 *   bruttoVal(350)   → 416.5  (nicht 416.5000000001)
 *   bruttoVal(100)   → 119
 *   bruttoVal(null)  → 0
 */
export const bruttoVal = (v: number | null | undefined): number => {
  const nettoCents = euroToCents(v);
  const mCents = mwstCents(nettoCents);
  return centsToEuro(nettoCents + mCents);
};

/**
 * Berechnet den MwSt-Anteil eines Netto-Euro-Betrags.
 * Intern via Cents-Pfad.
 *
 * Beispiele:
 *   mwstVal(350)   → 66.5
 *   mwstVal(100)   → 19
 *   mwstVal(null)  → 0
 */
export const mwstVal = (v: number | null | undefined): number => {
  return centsToEuro(mwstCents(euroToCents(v)));
};

/** Formatiert einen Netto-Betrag als "1.234,56 €". */
export const fmt = (v: number): string => {
  return (
    Number(v || 0)
      .toFixed(2)
      .replace('.', ',') + ' \u20ac'
  );
};

/** Formatiert einen Brutto-Betrag als "1.234,56 €". */
export const fmtBrutto = (v: number): string => {
  return bruttoVal(v).toFixed(2).replace('.', ',') + ' \u20ac';
};

/** Formatiert ein ISO-Datum (YYYY-MM-DD) als deutsches Format (DD.MM.YYYY). */
export const fmtDate = (d: string | null | undefined): string => {
  if (!d) return '\u2013';
  const parts = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return parts ? parts[3] + '.' + parts[2] + '.' + parts[1] : d;
};

/**
 * Gibt das aktuelle Datum als YYYY-MM-DD im LOKALEN Zeitzonen-Format zurück.
 *
 * WICHTIG: `new Date().toISOString().slice(0, 10)` ist UTC und liefert in
 * Deutschland (UTC+1 Winter / UTC+2 Sommer) zwischen 23:00/22:00 und
 * Mitternacht bereits den FOLGENDEN Tag — und zwischen Mitternacht und
 * 01:00/02:00 den VORHERIGEN Tag. Das verursacht falsche Rechnungs-Daten,
 * falsche Zertifikats-Ablauf-Prüfungen und inkonsistente Statistiken.
 *
 * Diese Funktion verwenden, wann immer ein "heute"-Datum als String
 * gespeichert, angezeigt oder verglichen wird.
 *
 * @returns Datum im Format "YYYY-MM-DD", z.B. "2026-04-09"
 */
export function todayLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

/**
 * Formatiert eine Date-Instanz als YYYY-MM-DD im LOKALEN Zeitzonen-Format.
 *
 * Gleicher Timezone-Bug-Schutz wie `todayLocal()`, aber für Dates, die bereits
 * konstruiert wurden (z.B. aus `new Date(); setMonth(+3)` für Abo-Enddatum-
 * Berechnung). Aufrufe wie `someDate.toISOString().slice(0, 10)` sind UTC
 * und können in Deutschland um Mitternacht den falschen Tag liefern.
 *
 * @param d - Date-Instanz.
 * @returns Datum im Format "YYYY-MM-DD", z.B. "2026-05-09", oder leerer
 *   String wenn die Instanz ungültig ist.
 */
export function dateToLocal(d: Date): string {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

// Locale-aware Datum/Zeit — ersetzt alle direkten toLocaleDateString/toLocaleTimeString Aufrufe

/** Formatiert ein Datum locale-bewusst (Default: de-DE). */
export function fmtDateLocale(
  d: string | Date,
  locale?: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!d) return '\u2013';
  try {
    return new Date(d).toLocaleDateString(locale || 'de-DE', opts);
  } catch (e) {
    return String(d);
  }
}

/** Formatiert eine Uhrzeit locale-bewusst als "HH:MM" (Default: de-DE). */
export function fmtTime(d: string | Date, locale?: string): string {
  if (!d) return '\u2013';
  try {
    return new Date(d).toLocaleTimeString(locale || 'de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return String(d);
  }
}

/** Formatiert Datum + Uhrzeit locale-bewusst (Default: de-DE). */
export function fmtDateTime(
  d: string | Date,
  locale?: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!d) return '\u2013';
  try {
    return new Date(d).toLocaleString(
      locale || 'de-DE',
      opts || {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
    );
  } catch (e) {
    return String(d);
  }
}

/** Locale-aware Zahl (1.234). */
export function fmtNum(v: number, locale?: string): string {
  return Number(v || 0).toLocaleString(locale || 'de-DE');
}

/** Bewertung (4,3). */
export function fmtRating(v: number): string {
  return Number(v || 0)
    .toFixed(1)
    .replace('.', ',');
}

/** Dateigröße (12 KB / 1,3 MB). */
export function fmtFileSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
  return (bytes / 1048576).toFixed(1).replace('.', ',') + ' MB';
}

/**
 * Postleitzahl, optional mit Länderkürzel davor („D-12345", „A-1010"):
 * DE 5-stellig, AT/CH/DK/BE/NL u. a. 4-stellig, PL „00-001", CZ/SK „110 00".
 */
const PLZ = '(?:[A-Z]{1,2}[-\\s]?)?(?:\\d{4,5}|\\d{2}-\\d{3}|\\d{3}\\s\\d{2})';
/** Teil der Form „PLZ Ort", z. B. „34128 Kassel". Gruppe 1 = Ort. */
const PLZ_ORT_RE = new RegExp('^' + PLZ + '\\s+(.+)$');
/** Teil, der NUR aus einer Postleitzahl besteht, z. B. „61169". */
const NUR_PLZ_RE = new RegExp('^' + PLZ + '$');

/**
 * Extrahiert den Ortsnamen aus einer vollständigen Adresse.
 *
 * Beispiele:
 *   "Hauptstr. 12, 10115 Berlin"                     → "Berlin"
 *   "Oberer Nordendweg 10, 34128 Kassel, Hessen"     → "Kassel"
 *   "Friedberg (Hessen), 61169"                      → "Friedberg (Hessen)"
 *   "ul. Marszałkowska 1, 00-001 Warszawa, mazowieckie" → "Warszawa"
 *   "München"                                        → "München"
 *   undefined/leer                                   → "–" (Gedankenstrich)
 *
 * Logik (07.10.2026):
 *   1. Von hinten den ersten Teil „PLZ Ort" suchen und den Ort nehmen.
 *      Grund: Adressvorschläge aus OpenStreetMap hängen das Bundesland an
 *      („…, 34128 Kassel, Hessen"). Bis 07.10.2026 wurde einfach der letzte
 *      Teil genommen — die Marktplatz-Karten zeigten dann „Hessen".
 *   2. Ohne „PLZ Ort": den letzten Teil, der nicht nur eine PLZ ist.
 *
 * Verwendet u.a. im Marktplatz (Datenschutz: Fahrer sieht nur Städte,
 * nicht die vollen Adressen der Firma), in den Push-Texten beim
 * Ausschreiben und im Tracking.
 */
export function extractCity(addr: string | null | undefined): string {
  if (!addr) return '\u2013';
  const parts = addr.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return '\u2013';
  for (let i = parts.length - 1; i >= 0; i--) {
    const m = PLZ_ORT_RE.exec(parts[i]);
    if (m) return m[1].trim();
  }
  for (let i = parts.length - 1; i >= 0; i--) {
    if (!NUR_PLZ_RE.test(parts[i])) return parts[i];
  }
  return parts[parts.length - 1];
}
